import React, { useState } from "react";
import {
    Box,
    Grid,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    TextField
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useNavigate } from "react-router-dom";
import AdminWidget from "../../components/admin/AdminWidget"; // adjust path
import TopTradersTable from "../../components/admin/TopTradersTable"; // adjust path
import { fetchWithAuth } from '../../utils/api';
import RecentItemsList from "../../components/admin/RecentItemsList";
import ItemsToRecalculateTable from "../../components/admin/ItemsToRecalculateTable";
import RecentTradesList from "../../components/admin/RecentTradesList";

const AdminDashboard = () => {
    const navigate = useNavigate();

    // --- Recent Trades state
    const [recentTrades, setRecentTrades] = useState([]);
    const [lastTradesUpdated, setLastTradesUpdated] = useState(null);
    const [loadingTrades, setLoadingTrades] = useState(false);
    const [disableRefreshTrades, setDisableRefreshTrades] = useState(false);

    // Limit input: store as string so it's a controlled text input
    const [tradesLimitInput, setTradesLimitInput] = useState(""); // "" => null/default 5

    const handleTradesLimitChange = (e) => {
        // keep digits only
        const digitsOnly = e.target.value.replace(/\D/g, "");
        if (digitsOnly === "") {
            setTradesLimitInput("");
            return;
        }
        // clamp to 100
        const num = Math.min(parseInt(digitsOnly, 10) || 0, 100);
        setTradesLimitInput(String(num));
    };

    const fetchRecentTrades = async () => {
        try {
            setLoadingTrades(true);

            const n = tradesLimitInput === "" ? null : parseInt(tradesLimitInput, 10);
            const limitParam = Number.isFinite(n) && n >= 1 ? `?limit=${n}` : "";
            const data = await fetchWithAuth(`/admin/recent-trades${limitParam}`);
            setRecentTrades(data);
            setLastTradesUpdated(new Date());

            // 2-minute cooldown
            setDisableRefreshTrades(true);
            setTimeout(() => setDisableRefreshTrades(false), 60 * 500);
        } catch (err) {
            console.error(err);
            setRecentTrades([]);
        } finally {
            setLoadingTrades(false);
        }
    };


    // --- items ---

    // --- Recent Items state
    const [recentItems, setRecentItems] = useState([]);
    const [lastItemsUpdated, setLastItemsUpdated] = useState(null);
    const [loadingItems, setLoadingItems] = useState(false);
    const [disableRefreshItems, setDisableRefreshItems] = useState(false);

    // Filters for items
    const [itemLimit, setItemLimit] = useState(10);
    const [itemType, setItemType] = useState("");

    // Fetch Recent Items
    const fetchRecentItems = async () => {
        try {
            setLoadingItems(true);
            const url = `/admin/recent-items?limit=${itemLimit}${itemType ? `&type=${itemType}` : ""}`;
            const data = await fetchWithAuth(url);
            setRecentItems(data);
            setLastItemsUpdated(new Date());
            setDisableRefreshItems(true);
            setTimeout(() => setDisableRefreshItems(false), 2 * 60 * 1000);
        } catch (err) {
            console.error(err);
            setRecentItems([]);
        } finally {
            setLoadingItems(false);
        }
    };

    // --- traders ---
    const [topTraders, setTopTraders] = useState([]);
    const [lastTradersUpdated, setLastTradersUpdated] = useState(null);
    const [loadingTraders, setLoadingTraders] = useState(false);
    const [disableRefreshTraders, setDisableRefreshTraders] = useState(false);

    const fetchTraders = async () => {
        try {
            setLoadingTraders(true);
            const data = await fetchWithAuth("/admin/top-traders");
            setTopTraders(data.slice(1));
            setLastTradersUpdated(new Date());

            // Disable refresh for 2 minutes
            setDisableRefreshTraders(true);
            setTimeout(() => setDisableRefreshTraders(false), 2 * 60 * 1000);
        } catch (err) {
            console.error(err);
            setTopTraders([]);
        } finally {
            setLoadingTraders(false);
        }
    };

    // --- Items to Recalc state
    const [itemsToRecalc, setItemsToRecalc] = useState([]);
    const [lastRecalcUpdated, setLastRecalcUpdated] = useState(null);
    const [loadingRecalc, setLoadingRecalc] = useState(false);
    const [disableRefreshRecalc, setDisableRefreshRecalc] = useState(false);

    const fetchItemsToRecalc = async () => {
        try {
            setLoadingRecalc(true);
            const data = await fetchWithAuth("/admin/items-to-recalculate");
            setItemsToRecalc(data);
            setLastRecalcUpdated(new Date());

            setDisableRefreshRecalc(true);
            setTimeout(() => setDisableRefreshRecalc(false), 2 * 60 * 1000);
        } catch (err) {
            console.error(err);
            setItemsToRecalc([]);
        } finally {
            setLoadingRecalc(false);
        }
    };

    return (
        <Box sx={{ px: 2, py: 3 }}>
            {/* Back Button */}
            <Button
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate("/admin")}
                sx={{ mb: 2 }}
            >
                Back to Admin Tools
            </Button>

            <Grid container spacing={2} columns={12}>
                <AdminWidget
                    title="Top Traders"
                    onRefresh={fetchTraders}
                    lastUpdated={lastTradersUpdated}
                    loading={loadingTraders}
                    disableRefresh={disableRefreshTraders}
                >
                    <TopTradersTable traders={topTraders} />
                </AdminWidget>
                <AdminWidget
                    title="Recent Items"
                    onRefresh={fetchRecentItems}
                    lastUpdated={lastItemsUpdated}
                    loading={loadingItems}
                    disableRefresh={disableRefreshItems}
                        width="twoThirds"
                >
                    <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
                        <FormControl size="small" sx={{ minWidth: 100 }}>
                            <InputLabel>Limit</InputLabel>
                            <Select
                                value={itemLimit}
                                label="Limit"
                                onChange={(e) => setItemLimit(e.target.value)}
                            >
                                {[10, 15, 25, 50, 75, 100].map((num) => (
                                    <MenuItem key={num} value={num}>{num}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControl size="small" sx={{ minWidth: 150 }}>
                            <InputLabel>Type</InputLabel>
                            <Select
                                value={itemType}
                                label="Type"
                                onChange={(e) => setItemType(e.target.value)}
                            >
                                <MenuItem value="">All</MenuItem>
                                <MenuItem value="nonwearable">Nonwearable</MenuItem>
                                <MenuItem value="wearable">Wearable</MenuItem>
                                <MenuItem value="special">Special</MenuItem>
                                <MenuItem value="token">Token</MenuItem>
                            </Select>
                        </FormControl>
                    </Box>
                    <RecentItemsList items={recentItems} />
                </AdminWidget>
                <AdminWidget
                    title="Items to be Recalculated"
                    onRefresh={fetchItemsToRecalc}
                    lastUpdated={lastRecalcUpdated}
                    loading={loadingRecalc}
                    disableRefresh={disableRefreshRecalc}
                >
                    <ItemsToRecalculateTable items={itemsToRecalc} />
                </AdminWidget>
                <AdminWidget
                    title="Most Recent Trades"
                    onRefresh={fetchRecentTrades}
                    lastUpdated={lastTradesUpdated}
                    loading={loadingTrades}
                    disableRefresh={disableRefreshTrades}
                    width={"twoThirds"}
                >
                    <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
                        <TextField
                            label="Limit (1-100)"
                            size="small"
                            value={tradesLimitInput}
                            onChange={handleTradesLimitChange}
                            inputProps={{
                                inputMode: "numeric",
                                pattern: "[0-9]*",
                                maxLength: 3,
                            }}
                            helperText={
                                tradesLimitInput === "" ? "Default is 5 if left blank" : ""
                            }
                            sx={{ width: 140 }}
                        />
                    </Box>

                    <RecentTradesList trades={recentTrades} />
                </AdminWidget>
            </Grid>
        </Box >
    );
};

export default AdminDashboard;
